import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { UiTranslationService } from '@core/services/ui-translation.service';
import { TrainFlashcard } from '../../../deck-train.models';
import { TRAINING_ANSWER_MAX_LENGTH } from '../../../training-input-limits';

@Component({
  selector: 'app-text-typed-or-voice-train-back',
  imports: [],
  templateUrl: './text-typed-or-voice-train-back.html',
  styleUrl: './text-typed-or-voice-train-back.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.text-typed-train-back-visible]': 'isBackVisible()',
    '[class.text-typed-train-back-has-media]': 'hasFrontMedia()',
  },
})
export class TextTypedOrVoiceTrainBack {
  private readonly deckTranslations =
    inject(UiTranslationService).namespace('deck');

  readonly card = input.required<TrainFlashcard>();
  readonly isBackVisible = input.required<boolean>();
  readonly isAudioFront = input(false);
  readonly hasFrontMedia = input(false);
  readonly isVideoFront = input(false);
  readonly isCorrect = input<boolean | null>(null);
  readonly userAnswer = input.required<string>();

  readonly continue = output<void>();
  readonly userAnswerChange = output<string>();

  readonly t = this.deckTranslations.t;
  readonly maxAnswerLength = TRAINING_ANSWER_MAX_LENGTH;
  readonly isAnswerTooLong = computed(
    () => this.userAnswer().length > this.maxAnswerLength,
  );
  readonly inputLang = computed(() =>
    this.resolveInputLanguage(this.card().languageCodeProvider),
  );
  readonly inputSpellcheckEnabled = computed(() => this.inputLang() !== null);

  onAnswerInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement | null;
    const value = inputElement?.value ?? '';
    this.userAnswerChange.emit(value);
  }

  continueAnswer(): void {
    if (!this.isAnswerTooLong()) this.continue.emit();
  }

  private resolveInputLanguage(
    languageCodeProvider: string | null | undefined,
  ): string | null {
    const candidate = (languageCodeProvider ?? '').trim().replace(/_/g, '-');
    if (!candidate) {
      return null;
    }

    if (!/^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/.test(candidate)) {
      return null;
    }

    const segments = candidate.split('-');
    return segments
      .map((segment, index) => {
        if (index === 0) {
          return segment.toLowerCase();
        }

        if (segment.length === 2 && /^[A-Za-z]{2}$/.test(segment)) {
          return segment.toUpperCase();
        }

        if (segment.length === 4 && /^[A-Za-z]{4}$/.test(segment)) {
          return `${segment[0].toUpperCase()}${segment.slice(1).toLowerCase()}`;
        }

        return segment.toLowerCase();
      })
      .join('-');
  }
}
